import { requestJSON } from '@codametrix/client-api';
import { cmxDateTime, dictionaryUtilities } from '@codametrix/ui-common';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';

import {
  NOTE_TEMPLATES_CONFIGURATION_KEY,
  type NoteTemplatesConfig,
  parseNoteTemplatesConfig,
  type ResolvedNoteTemplate,
  selectNoteTemplate
} from './note-template';
import {
  type DictionaryRow,
  NOTE_TYPE_DICTIONARY_NAME,
  resolveCmxNoteType
} from './note-type-resolution';

export function noteTypeDictionaryParams(
  tenantId: string,
  effectiveDate: string
): URLSearchParams {
  return new URLSearchParams({
    tenantId,
    derivedIndicator: 'true',
    effectiveDate,
    dictionaryName: NOTE_TYPE_DICTIONARY_NAME
  });
}

/**
 * configuration-service's resolved read: tenant hierarchy, service-line aliasing (BedsidePro ->
 * Inpatient Professional Coding) and the `*` fallback all happen server side, so the value that
 * comes back is the one that applies.
 */
export function noteTemplatesConfigurationPath(
  tenantId: string,
  serviceLine: string | null | undefined
): string {
  const query = serviceLine
    ? `?${new URLSearchParams({ serviceLine }).toString()}`
    : '';
  return `/configuration/${NOTE_TEMPLATES_CONFIGURATION_KEY}/${encodeURIComponent(tenantId)}/v2${query}`;
}

type ResolvedConfiguration = { values?: { jsonValue?: unknown }[] | null };

export function toDictionaryRows(
  rowValues: readonly Record<string, string | null>[] | null | undefined
): DictionaryRow[] {
  return (rowValues ?? []).map((values) => ({
    cells: Object.entries(values).map(([columnName, value]) => ({
      columnName,
      value
    }))
  }));
}

function loadNoteTypeRows(tenantId: string): Promise<readonly DictionaryRow[]> {
  const today = cmxDateTime.format(new Date(), cmxDateTime.FORMATS.DATE);
  return dictionaryUtilities
    .fetchDictionaryRows<Record<string, string | null>>(
      noteTypeDictionaryParams(tenantId, today)
    )
    .then((data) => toDictionaryRows(data?.rowValues));
}

function loadNoteTemplates(
  tenantId: string,
  serviceLine: string | null | undefined
): Promise<NoteTemplatesConfig | null> {
  return requestJSON<ResolvedConfiguration>(
    noteTemplatesConfigurationPath(tenantId, serviceLine)
  ).then((config) => parseNoteTemplatesConfig(config?.values?.[0]?.jsonValue));
}

export type NoteTemplateData = {
  /** The tenant's "Note Type" dictionary; empty until loaded, and when the read failed. */
  rows: readonly DictionaryRow[];
  /** `null` until loaded, when none is configured, and when the read failed -- all mean note order. */
  config: NoteTemplatesConfig | null;
  loading: boolean;
  error: Error | null;
};

const NO_DATA: NoteTemplateData = {
  rows: [],
  config: null,
  loading: false,
  error: null
};

const NoteTemplateContext = createContext<NoteTemplateData>(NO_DATA);

/**
 * Loads the note-type dictionary and the template configuration once for a tenant and service
 * line, and serves every note's lookup from that. Mounted above the notes (the coding screen), so
 * a case with many notes reads each once rather than once per note.
 *
 * Nothing here is required to render a note: a failed read leaves rows empty and config null, and
 * is reported on `error` for a consumer that wants it, never thrown.
 */
export const NoteTemplateProvider: React.FC<
  React.PropsWithChildren<{
    tenantId: string | null | undefined;
    serviceLine: string | null | undefined;
  }>
> = ({ tenantId, serviceLine, children }) => {
  const [data, setData] = useState<NoteTemplateData>(NO_DATA);

  useEffect(() => {
    if (!tenantId) {
      setData(NO_DATA);
      return;
    }

    let current = true;
    setData({ ...NO_DATA, loading: true });

    Promise.all([
      loadNoteTypeRows(tenantId),
      loadNoteTemplates(tenantId, serviceLine)
    ])
      .then(([rows, config]) => {
        if (current) {
          setData({ rows, config, loading: false, error: null });
        }
      })
      .catch((caught: unknown) => {
        if (current) {
          setData({
            ...NO_DATA,
            error: caught instanceof Error ? caught : new Error(String(caught))
          });
        }
      });

    return () => {
      current = false;
    };
  }, [tenantId, serviceLine]);

  return (
    <NoteTemplateContext.Provider value={data}>
      {children}
    </NoteTemplateContext.Provider>
  );
};

export type NoteTemplateInput = {
  /** The note's raw type code, as received. Often absent; the description carries the lookup. */
  noteType?: string | null;
  /** The note's raw description (its title), as received. */
  noteDescription?: string | null;
};

export type NoteTemplateState = {
  /** `null` while loading, and whenever no template applies -- both mean note order. */
  template: ResolvedNoteTemplate | null;
  /** The CMX note type the dictionary landed on, or `null` when it was not looked up. */
  cmxNoteType: string | null;
  loading: boolean;
  error: Error | null;
};

/**
 * Resolves the template for one note from what `NoteTemplateProvider` loaded: `(note type,
 * description)` -> CMX note type through the "Note Type" dictionary, then CMX note type -> template
 * through the configuration. Resolution is per note, so two notes of one case can differ.
 *
 * With no provider mounted, no type or description to look up, an unrecognised note type or a note
 * type with no template, `template` is null and the viewer falls back to note order.
 */
export function useNoteTemplate({
  noteType,
  noteDescription
}: NoteTemplateInput): NoteTemplateState {
  const { rows, config, loading, error } = useContext(NoteTemplateContext);

  return useMemo(() => {
    // With neither value there is nothing identifying the note, and the dictionary's wildcard rows
    // would match it to whatever they say; no template is the honest answer.
    if (!config || (!noteType && !noteDescription)) {
      return { template: null, cmxNoteType: null, loading, error };
    }
    const cmxNoteType = resolveCmxNoteType(rows, noteType, noteDescription);
    return {
      template: selectNoteTemplate(config, cmxNoteType),
      cmxNoteType,
      loading,
      error
    };
  }, [rows, config, loading, error, noteType, noteDescription]);
}
